import { DocumentNode, ExecutionResult } from "graphql";
import { NotificationInstance } from "antd/lib/notification";

export type ServerParseError = Error & {
  response: Response;
  statusCode: number;
  bodyText: string;
};

export type ServerError = Error & {
  response: Response;
  result: Record<string, any>;
  statusCode: number;
};

export interface Operation {
  query: DocumentNode;
  variables: Record<string, any>;
  operationName: string;
  extensions: Record<string, any>;
  setContext: (context: Record<string, any>) => Record<string, any>;
  getContext: () => Record<string, any>;
}

export enum ActionTypes {
  REQUEST = "REQUEST",
  ERROR = "ERROR",
  SUCCESS = "SUCCESS",
  CANCEL = "CANCEL",
}
export default ActionTypes;

export interface Action {
  type: ActionTypes;
}

export interface NetworkStatusActionRequest extends Action {
  type: typeof ActionTypes.REQUEST;
  payload: {
    operation: Operation;
  };
}

export interface NetworkStatusActionError extends Action {
  type: typeof ActionTypes.ERROR;
  payload: {
    operation: Operation;
    networkError: Error | ServerError | ServerParseError;
  };
}

export interface NetworkStatusActionSuccess extends Action {
  type: typeof ActionTypes.SUCCESS;
  payload: {
    operation: Operation;
    result: ExecutionResult;
  };
}

export interface NetworkStatusActionCancel extends Action {
  type: typeof ActionTypes.CANCEL;
  payload: {
    operation: Operation;
  };
}

export type NetworkStatusAction =
  | NetworkStatusActionRequest
  | NetworkStatusActionError
  | NetworkStatusActionSuccess
  | NetworkStatusActionCancel;

export interface Operation {
  query: DocumentNode;
  variables: Record<string, any>;
  operationName: string;
  extensions: Record<string, any>;
  setContext: (context: Record<string, any>) => Record<string, any>;
  getContext: () => Record<string, any>;
}

export type NotificationAction = keyof NotificationInstance;

export const TYPE_TO_ACTION_STRING: Record<ActionTypes, NotificationAction> = {
  [ActionTypes.SUCCESS]: "success",
  [ActionTypes.ERROR]: "error",
  [ActionTypes.CANCEL]: "info",
  [ActionTypes.REQUEST]: "info",
};

export type VPDNetworkStatusAction = Omit<NetworkStatusAction, "payload"> & {
  payload: {
    operation: Operation | undefined;
  };
};

const MATTER_ENTITY = "Matter";
const EXTERNAL_MATTER_ENTITY = "External Matter";
const MATTER_VERSION_ENTITY = "Matter Version";
const ASSUMPTION_ENTITY = "Assumption";
const ASSUMPTIONS_ENTITIES = `${ASSUMPTION_ENTITY}s`;
const EXCLUSION_ENTITY = "Exclusion";
const EXCLUSIONS_ENTITIES = `${EXCLUSION_ENTITY}s`;
const PHASE_ENTITY = "Phase";
const PHASES_ENTITIES = `${PHASE_ENTITY}s`;
const TASK_ENTITY = "Task";
const TASKS_ENTITIES = `${TASK_ENTITY}s`;
const CLIENT_ENTITY = "Client";
//const CLIENTS_ENTITIES = `${CLIENT_ENTITY}s`;
const SETTINGS_ENTITY = "Settings";
const DISBURSEMENT_ENTITY = "Disbursement";
const DISBURSEMENTS_ENTITIES = `${DISBURSEMENT_ENTITY}s`;
const UPLIFT_ENTITY = "Uplift";
const UPLIFTS_ENTITIES = `${UPLIFT_ENTITY}s`;
const DISCOUNT_ENTITY = "Discount";
const DISCOUNTS_ENTITIES = `${DISCOUNT_ENTITY}s`;
const TECHNOLOGY_CHARGE_ENTITY = "Technology";
const TECHNOLOGY_CHARGES_ENTITIES = `Technologies`;
const EXTERNAL_SYSTEM_DATA_IMPORT_ENTITY = "External System Data Import";
const FILE_DATA_IMPORT_ENTITY = "File Data Import";
const RATE_ENTITY = "Rate";
const WORK_ENTITY = "Work";
const LIBRARY_PHASE_ENTITY = "Library Phase";
//const LIBRARY_PHASES_ENTITIES = `${LIBRARY_PHASE_ENTITY}s`;
const MATTER_PHASE_ENTITY = "Matter Phase";
const MATTER_PHASES_ENTITIES = `${MATTER_PHASE_ENTITY}s`;
const NOTIFICATION_ENTITY = "Notification";
const NOTIFICATIONS_ENTITIES = `${NOTIFICATION_ENTITY}s`;
const NOTIFICATION_RULE_ENTITY = `${NOTIFICATION_ENTITY} Rule`;
const MATTER_TEMPLATE_ENTITY = "Matter Template";
const MATTER_COLLABORATORS_ENTITY = "Matter Collaborators";
const BRAND_ENTITY = "Brand";

const LOGGED_OUT = "Logged Out";

const CREATED = "created";
const UPDATED = "updated";
const DELETED = "deleted";
const COPIED = "copied";

export const NetworkActionToHumanReadable = {
  createExternalMatter: `${EXTERNAL_MATTER_ENTITY} ${CREATED}`,
  updateExternalMatter: `${EXTERNAL_MATTER_ENTITY} ${UPDATED}`,
  createMatter: `${MATTER_ENTITY} ${CREATED}`,
  createMatterFromTemplate: `${MATTER_ENTITY} ${CREATED} from template`,
  updateMatter: `${MATTER_ENTITY} ${UPDATED}`,
  revertToVersion: `${MATTER_VERSION_ENTITY} reverted`,
  createAssumptions: `${ASSUMPTIONS_ENTITIES} ${CREATED}`,
  updateAssumption: `${ASSUMPTION_ENTITY} ${UPDATED}`,
  updateAssumptions: `${ASSUMPTIONS_ENTITIES} ${UPDATED}`,
  deleteAssumptions: `${ASSUMPTIONS_ENTITIES} ${DELETED}`,
  createExclusions: `${EXCLUSIONS_ENTITIES} ${CREATED}`,
  updateExclusion: `${EXCLUSION_ENTITY} ${UPDATED}`,
  updateExclusions: `${EXCLUSIONS_ENTITIES} ${UPDATED}`,
  deleteExclusions: `${EXCLUSIONS_ENTITIES} ${DELETED}`,
  createAndAddPhase: `${PHASE_ENTITY} ${CREATED}`,
  updatePhase: `${PHASE_ENTITY} ${UPDATED}`,
  updatePhases: `${PHASES_ENTITIES} ${UPDATED}`,
  deletePhase: `${PHASE_ENTITY} ${DELETED}`,
  createAndAddTask: `${TASKS_ENTITIES} ${CREATED}`,
  updateTask: `${TASK_ENTITY} ${UPDATED}`,
  updateTasks: `${TASKS_ENTITIES} ${UPDATED}`,
  deleteTask: `${TASK_ENTITY} ${DELETED}`,
  createClient: `${CLIENT_ENTITY} ${CREATED}`,
  updateClient: `${CLIENT_ENTITY} ${UPDATED}`,
  updateSettings: `${SETTINGS_ENTITY} ${UPDATED}`,
  createAndAddDisbursement: `${DISBURSEMENT_ENTITY} ${CREATED}`,
  updateDisbursement: `${DISBURSEMENT_ENTITY} ${UPDATED}`,
  updateDisbursements: `${DISBURSEMENTS_ENTITIES} ${UPDATED}`,
  deleteDisbursement: `${DISBURSEMENT_ENTITY} ${DELETED}`,
  createAndAddUplift: `${UPLIFT_ENTITY} ${CREATED}`,
  updateUplift: `${UPLIFT_ENTITY} ${UPDATED}`,
  updateUplifts: `${UPLIFTS_ENTITIES} ${UPDATED}`,
  deleteUplift: `${UPLIFT_ENTITY} ${DELETED}`,
  createAndAddDiscount: `${DISCOUNT_ENTITY} ${CREATED}`,
  updateDiscount: `${DISCOUNT_ENTITY} ${UPDATED}`,
  updateDiscounts: `${DISCOUNTS_ENTITIES} ${UPDATED}`,
  deleteDiscount: `${DISCOUNT_ENTITY} ${DELETED}`,
  createAndAddTechnologyCharge: `${TECHNOLOGY_CHARGE_ENTITY} ${CREATED}`,
  updateTechnologyCharge: `${TECHNOLOGY_CHARGE_ENTITY} ${UPDATED}`,
  updateTechnologyCharges: `${TECHNOLOGY_CHARGES_ENTITIES} ${UPDATED}`,
  deleteTechnologyCharge: `${TECHNOLOGY_CHARGE_ENTITY} ${DELETED}`,
  createExternalSystemDataImport: `${EXTERNAL_SYSTEM_DATA_IMPORT_ENTITY} ${CREATED}`,
  createFileDataImport: `${FILE_DATA_IMPORT_ENTITY} ${CREATED}`,
  createRate: `${RATE_ENTITY} ${CREATED}`,
  createAndAddWork: `${WORK_ENTITY} ${CREATED}`,
  updateWork: `${WORK_ENTITY} ${UPDATED}`,
  deleteWork: `${WORK_ENTITY} ${DELETED}`,
  copyLibraryPhase: `${LIBRARY_PHASE_ENTITY} ${COPIED}`,
  copyMatterPhases: `${MATTER_PHASES_ENTITIES} ${COPIED}`,
  createNotificationRule: `${NOTIFICATION_RULE_ENTITY} ${CREATED}`,
  updateNotificationRule: `${NOTIFICATION_RULE_ENTITY} ${UPDATED}`,
  deleteNotificationRule: `${NOTIFICATION_RULE_ENTITY} ${DELETED}`,
  deleteNotification: `${NOTIFICATION_ENTITY} ${DELETED}`,
  deleteAllNotifications: `All ${NOTIFICATIONS_ENTITIES} ${DELETED}`,
  createMatterTemplate: `${MATTER_TEMPLATE_ENTITY} ${CREATED}`,
  updateMatterTemplate: `${MATTER_TEMPLATE_ENTITY} ${UPDATED}`,
  updateMatterCollaborators: `${MATTER_COLLABORATORS_ENTITY} ${UPDATED}`,
  logout: `${LOGGED_OUT}`,
  createBrand: `${BRAND_ENTITY} ${CREATED}`,
  updateBrand: `${BRAND_ENTITY} ${UPDATED}`,
  deleteBrand: `${BRAND_ENTITY} ${DELETED}`,
  createExternalMatter_Failed: `${EXTERNAL_MATTER_ENTITY} not ${CREATED}`,
  updateExternalMatter_Failed: `${EXTERNAL_MATTER_ENTITY} not ${UPDATED}`,
  createMatter_Failed: `${MATTER_ENTITY} not ${CREATED}`,
  createMatterFromTemplate_Failed: `${MATTER_ENTITY} not ${CREATED} from template`,
  updateMatter_Failed: `${MATTER_ENTITY} not ${UPDATED}`,
  revertToVersion_Failed: `${MATTER_VERSION_ENTITY} not reverted`,
  createAssumptions_Failed: `${ASSUMPTIONS_ENTITIES} not ${CREATED}`,
  updateAssumption_Failed: `${ASSUMPTION_ENTITY} not ${UPDATED}`,
  updateAssumptions_Failed: `${ASSUMPTIONS_ENTITIES} not ${UPDATED}`,
  deleteAssumptions_Failed: `${ASSUMPTIONS_ENTITIES} not ${DELETED}`,
  createExclusions_Failed: `${EXCLUSIONS_ENTITIES} not ${CREATED}`,
  updateExclusion_Failed: `${EXCLUSION_ENTITY} not ${UPDATED}`,
  updateExclusions_Failed: `${EXCLUSIONS_ENTITIES} not ${UPDATED}`,
  deleteExclusions_Failed: `${EXCLUSIONS_ENTITIES} not ${DELETED}`,
  createAndAddPhase_Failed: `${PHASE_ENTITY} not ${CREATED}`,
  updatePhase_Failed: `${PHASE_ENTITY} not ${UPDATED}`,
  updatePhases_Failed: `${PHASES_ENTITIES} not ${UPDATED}`,
  deletePhase_Failed: `${PHASE_ENTITY} not ${DELETED}`,
  createAndAddTask_Failed: `${TASKS_ENTITIES} not ${CREATED}`,
  updateTask_Failed: `${TASK_ENTITY} not ${UPDATED}`,
  updateTasks_Failed: `${TASKS_ENTITIES} not ${UPDATED}`,
  deleteTask_Failed: `${TASK_ENTITY} not ${DELETED}`,
  createClient_Failed: `${CLIENT_ENTITY} not ${CREATED}`,
  updateClient_Failed: `${CLIENT_ENTITY} not ${UPDATED}`,
  updateSettings_Failed: `${SETTINGS_ENTITY} not ${UPDATED}`,
  createAndAddDisbursement_Failed: `${DISBURSEMENT_ENTITY} not ${CREATED}`,
  updateDisbursement_Failed: `${DISBURSEMENT_ENTITY} not ${UPDATED}`,
  updateDisbursements_Failed: `${DISBURSEMENTS_ENTITIES} not ${UPDATED}`,
  deleteDisbursement_Failed: `${DISBURSEMENT_ENTITY} not ${DELETED}`,
  createAndAddUplift_Failed: `${UPLIFT_ENTITY} not ${CREATED}`,
  updateUplift_Failed: `${UPLIFT_ENTITY} not ${UPDATED}`,
  updateUplifts_Failed: `${UPLIFTS_ENTITIES} not ${UPDATED}`,
  deleteUplift_Failed: `${UPLIFT_ENTITY} not ${DELETED}`,
  createAndAddDiscount_Failed: `${DISCOUNT_ENTITY} not ${CREATED}`,
  updateDiscount_Failed: `${DISCOUNT_ENTITY} not ${UPDATED}`,
  updateDiscounts_Failed: `${DISCOUNTS_ENTITIES} not ${UPDATED}`,
  deleteDiscount_Failed: `${DISCOUNT_ENTITY} not ${DELETED}`,
  createAndAddTechnologyCharge_Failed: `${TECHNOLOGY_CHARGE_ENTITY} not ${CREATED}`,
  updateTechnologyCharge_Failed: `${TECHNOLOGY_CHARGE_ENTITY} not ${UPDATED}`,
  updateTechnologyCharges_Failed: `${TECHNOLOGY_CHARGES_ENTITIES} not ${UPDATED}`,
  deleteTechnologyCharge_Failed: `${TECHNOLOGY_CHARGE_ENTITY} not ${DELETED}`,
  createExternalSystemDataImport_Failed: `${EXTERNAL_SYSTEM_DATA_IMPORT_ENTITY} not ${CREATED}`,
  createFileDataImport_Failed: `${FILE_DATA_IMPORT_ENTITY} not ${CREATED}`,
  createRate_Failed: `${RATE_ENTITY} not ${CREATED}`,
  createAndAddWork_Failed: `${WORK_ENTITY} not ${CREATED}`,
  updateWork_Failed: `${WORK_ENTITY} not ${UPDATED}`,
  deleteWork_Failed: `${WORK_ENTITY} not ${DELETED}`,
  copyLibraryPhase_Failed: `${LIBRARY_PHASE_ENTITY} not ${COPIED}`,
  copyMatterPhases_Failed: `${MATTER_PHASES_ENTITIES} not ${COPIED}`,
  createNotificationRule_Failed: `${NOTIFICATION_RULE_ENTITY} not ${CREATED}`,
  updateNotificationRule_Failed: `${NOTIFICATION_RULE_ENTITY} not ${UPDATED}`,
  deleteNotificationRule_Failed: `${NOTIFICATION_RULE_ENTITY} not ${DELETED}`,
  deleteNotification_Failed: `${NOTIFICATION_ENTITY} not ${DELETED}`,
  deleteAllNotifications_Failed: `All ${NOTIFICATIONS_ENTITIES} not ${DELETED}`,
  createMatterTemplate_Failed: `${MATTER_TEMPLATE_ENTITY} not ${CREATED}`,
  updateMatterTemplate_Failed: `${MATTER_TEMPLATE_ENTITY} not ${UPDATED}`,
  updateMatterCollaborators_Failed: `${MATTER_COLLABORATORS_ENTITY} not ${UPDATED}`,
  logout_Failed: `Not ${LOGGED_OUT}`,
};

export type NetworkActionHumanReadable_NetworkName = keyof typeof NetworkActionToHumanReadable;

export type NetworkActionHumanReadable_HumanReadableValue = ValueOf<
  typeof NetworkActionToHumanReadable
>;

export type ValueOf<T> = T[keyof T];

export enum GenericCUD {
  CREATED = "Created",
  UPDATED = "Updated",
  DELETED = "Deleted",
  COPIED = "Copied",
  UNKNOWN = "Unknown",
}
